<template>
    <div>
        <div
            v-if="shouldShowGuide && !isChatbotPageActive && !allCompleted"
            class="setup-guide-container"
            :class="{ expanded: showDetails }"
        >
            <div class="floating-button" @click="toggleDetails">
                <div class="progress-indicator">
                    <svg width="32" height="32" viewBox="0 0 48 48">
                        <circle class="progress-bg" cx="24" cy="24" r="20" stroke-width="4" />
                        <circle
                            class="progress-bar"
                            cx="24"
                            cy="24"
                            r="20"
                            stroke-width="4"
                            :stroke-dasharray="circumference"
                            :stroke-dashoffset="progressOffset"
                        />
                    </svg>
                    <span class="progress-text">{{ completionPercentage }}%</span>
                </div>
                <div v-if="showDetails" class="guide-title">{{ $t("setupWizard.supsisSetupGuide") }}</div>
                <i class="material-icons toggle-icon">{{
                    showDetails ? "keyboard_arrow_down" : "keyboard_arrow_up"
                }}</i>
            </div>

            <div v-if="showDetails" class="setup-panel">
                <div class="panel-header">
                    <p class="font-weight-bold">{{ $t("setupWizard.supsisSetupGuideDescription") }}</p>
                </div>

                <div class="setup-items">
                    <div
                        v-for="(item, index) in enabledSetupItems"
                        :key="item.key"
                        class="setup-item"
                        :class="{ completed: item.completed }"
                    >
                        <div class="item-status">
                            <div v-if="item.completed" class="completed-icon">✓</div>
                            <div v-else class="step-number">{{ index + 1 }}</div>
                        </div>
                        <div class="item-content">
                            <span>{{ item.showItem }}</span>
                            <h4>{{ item.title }}</h4>
                            <p>{{ item.description }}</p>
                            <div v-if="!item.completed" @click="startTour(item.key)">
                                <router-link :to="{ path: `/${item.routerUrl}` }" class="setup-link">
                                    {{ $t("setupWizard.goToSetup") }}
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="footer">
                    <span class="footer-text">
                        <i class="material-icons footer-icon">info</i>
                        {{ $t("setupWizard.completionPercentageDescription", { percentage: completionPercentage }) }}
                    </span>
                </div>
            </div>
        </div>
        <setup-tour v-if="showTour" :steps="tourSteps" :is-active="showTour" @tour-finished="onTourFinished" />
        <finish-tour-modal :is-visible="showFinishModal" :tour-key="currentTourKey" @close="closeFinishModal" />
    </div>
</template>
<script>
import { getSetupItems } from "./setupItems";
import { getTourStepsForItem } from "./stepItemsMap";
import SetupTour from "./SetupTour.vue";
import FinishTourModal from "./FinishTourModal.vue";
import { mapState } from "vuex";

export default {
    name: "SetupWizard",
    components: {
        SetupTour,
        FinishTourModal,
    },
    props: {
        lobbyData: {
            type: Object,
            default: () => ({}),
        },
        createdDate: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            showDetails: false,
            currentTourKey: null,
            setupItems: [],
            showTour: false,
            tourSteps: [],
            showFinishModal: false,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
        }),
        enabledSetupItems() {
            return this.setupItems
                .filter((item) => item.isEnabled === true)
                .sort((a, b) => {
                    if (a.completed === b.completed) return 0;
                    return a.completed ? 1 : -1;
                });
        },
        completedSetups() {
            return this.enabledSetupItems.filter((item) => item.completed).length;
        },
        completionPercentage() {
            if (!this.enabledSetupItems.length) return 0;
            return Math.round((this.completedSetups / this.enabledSetupItems.length) * 100);
        },
        circumference() {
            return 2 * Math.PI * 20;
        },
        progressOffset() {
            return this.circumference - (this.completionPercentage / 100) * this.circumference;
        },
        allCompleted() {
            return this.enabledSetupItems.every((item) => item.completed);
        },
        isRecent() {
            if (!this.createdDate) return false;
            //<!-- todo: burası kaç günlük panellerde gösterileceği değişkendir -->
            const DAYS_TO_SHOW = 999;
            const dateLimit = new Date();
            dateLimit.setDate(dateLimit.getDate() - DAYS_TO_SHOW);
            return new Date(this.createdDate) > dateLimit;
        },
        shouldShowGuide() {
            return this.isRecent;
        },
        isChatbotPageActive() {
            return (
                this.$route &&
                (
                    this.$route.path.startsWith('/chatbot/story/') ||
                    this.$route.path.startsWith('/module') ||
                    this.$route.path.startsWith('/settings') ||
                    this.$route.path.startsWith('/tables/t/') ||
                    this.$route.path.startsWith('/automations/') && this.$route.path.includes('/design')
                )
            );
        },
    },
    watch: {
        lobbyData: {
            handler(newVal) {
                if (newVal && Object.keys(newVal).length > 0) {
                    this.setSetupItems();
                }
            },
            immediate: true,
            deep: true,
        },
        allCompleted(newVal) {
            if (newVal) {
                this.showDetails = false;
            }
        },
        user: {
            deep: true,
            handler(newVal) {
                if (newVal && newVal.guideTutorials) {
                    this.setSetupItems();
                }
            },
        },
        '$i18n.locale'(newLocale) {
            this.setSetupItems();
        },
    },
    methods: {
        onTourFinished() {
            this.showTour = false;
            this.showFinishModal = true;
        },
        toggleDetails() {
            this.showDetails = !this.showDetails;
        },
        startTour(key) {
            const item = this.enabledSetupItems.find((item) => item.key === key);
            if (!item) return;

            this.tourSteps = getTourStepsForItem(key, this.lobbyData, this);
            this.currentTourKey = key;
            this.showTour = true;
            this.showDetails = false;
        },
        setSetupItems() {
            if (!this.lobbyData) return;
            const { systemOptions, moduleSelections, moduleSelectionsTwo } = this.lobbyData;
            this.setupItems = getSetupItems(this.user.guideTutorials, systemOptions, moduleSelections, moduleSelectionsTwo, this);
        },
        hideGuide() {
            this.showDetails = false;
        },
        closeFinishModal() {
            this.showFinishModal = false;
            this.currentTourKey = null;
        },
    },
};
</script>
<style lang="scss" scoped>
.setup-guide-container {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 500;
}

.floating-button {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #ff9800;
    color: white;
    border-radius: 15px;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    transition: all 0.3s ease;

    &:hover {
        background-color: #e68a00;
        box-shadow: 0 6px 16px rgba(255, 152, 0, 0.4);
    }
}

.progress {
    &-indicator {
        position: relative;
        width: 32px;
        height: 32px;
        margin-right: 6px;
    }

    &-bg {
        fill: none;
        stroke: rgba(255, 255, 255, 0.2);
    }

    &-bar {
        fill: none;
        stroke: white;
        stroke-linecap: round;
        transform: rotate(-90deg);
        transform-origin: 50% 50%;
        transition: stroke-dashoffset 0.5s ease;
    }

    &-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 10px;
        font-weight: bold;
    }
}

.button-text {
    font-size: 14px;
    font-weight: 500;
}

.toggle-icon {
    margin-left: 8px;
    font-size: 24px;
    font-weight: bold;
    transition: transform 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.setup {
    &-panel {
        width: 360px;
        margin-top: 5px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        overflow: hidden;
    }

    &-items {
        max-height: 400px;
        overflow-y: auto;
    }

    &-item {
        display: flex;
        padding: 16px;
        border-bottom: 1px solid #f0f2f5;
        transition: background 0.2s ease;

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background-color: #f9faff;
        }
    }

    &-link {
        display: inline-block;
        font-size: 12px;
        font-weight: 600;
        color: #4a6bff;
        text-decoration: none;
        padding: 4px 0;

        &:hover {
            text-decoration: underline;
        }

        &.completed {
            color: #4caf50;
        }
    }
}

.panel-header {
    padding: 20px;
    background: linear-gradient(135deg, #ff9800 0%, #e68a00 100%);
    color: white;

    p {
        margin: 8px 0 0;
        font-size: 15px;
        opacity: 0.9;
    }
}

.guide-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: white;
}

.item {
    &-status {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        margin-right: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &-content {
        flex-grow: 1;

        h4 {
            margin: 0 0 4px;
            font-size: 14px;
            font-weight: 600;
            color: #2d3748;
        }

        p {
            margin: 0 0 8px;
            font-size: 13px;
            line-height: 1.4;
        }
    }
}

.step-number {
    width: 24px;
    height: 24px;
    background-color: #e0e6ff;
    color: #4a6bff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
}

.completed-icon {
    width: 24px;
    height: 24px;
    background-color: #4caf50;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
}

.footer {
    padding: 10px;
    background-color: #f0f2f5;
    border-top: 1px solid #e0e6ff;

    .footer-text {
        display: flex;
        align-items: center;
        font-size: 12px;
        font-weight: 600;
        color: #2d3748;
    }

    .footer-icon {
        color: lightslategray;
        margin-right: 4px;
        font-size: 18px;
    }
}
</style>
